/**
 * Mission Control client for the AHP relay path.
 *
 * Mints the Web PubSub credentials a browser client needs to open a relay
 * connection to an environment's agent daemon. The connect endpoint returns a
 * short-lived token plus the per-client group lanes (`broadcast`, `to_client`,
 * `to_host`) the WPS transport joins and publishes to.
 *
 * The network call is supplied by the caller as an {@link MCFetcher} so this
 * package stays free of any host application's auth / base-URL handling.
 */

import {parseGroupName, type ClientLaneKind} from '../webpubsub/groups'

/** API path that mints WPS credentials for a client connection, with session context for repo-scoped token minting. */
export function environmentConnectApiPath(environmentId: string, sessionId: string): string {
  const params = new URLSearchParams({session_id: sessionId})
  return `/agents/environments/${encodeURIComponent(environmentId)}/connect?${params.toString()}`
}

/** API path that refreshes WPS credentials for an existing relay client connection. */
export function environmentReconnectApiPath(environmentId: string, clientId: string): string {
  const params = new URLSearchParams({client_id: clientId})
  return `/agents/environments/${encodeURIComponent(environmentId)}/reconnect?${params.toString()}`
}

/** Per-client group lanes the WPS transport joins / publishes to. */
export interface MCConnectGroups {
  broadcast: string
  to_client: string
  to_host: string
}

// A public sealing key the host advertises and MC reseals the user's GitHub
// token to. The `key_id` is also embedded in the sealed envelope.
export interface AdvertisedEncryptionKey {
  key_id: string
  use: string
  algorithm: string
  public_key: string
}

/** Response from the connect/reconnect endpoints — everything needed to open WPS. */
export interface MCConnectResponse {
  access_token: string
  expires_at: string
  wps_endpoint: string
  hub: string
  subprotocol: string
  client_id: string
  groups: MCConnectGroups
  // The user's GitHub token resealed to the host's auth-token key, forwarded
  // verbatim as the AHP `authenticate` token. Absent if the host advertised no key.
  encrypted_github_token?: string
  host_encryption_key?: AdvertisedEncryptionKey
}

export class MissionControlError extends Error {
  readonly status?: number
  readonly code?: string

  constructor(message: string, status?: number, code?: string) {
    super(message)
    this.status = status
    this.code = code
    this.name = 'MissionControlError'
  }
}

/**
 * Abstraction over the network call, supplied by the consuming application.
 * Implementations must forward `options.signal` to the underlying request so
 * disposal and wake deadlines can cancel in-flight work.
 */
export interface MCFetchOptions {
  signal?: AbortSignal
}

export type MCFetcher = (path: string, options?: MCFetchOptions) => Promise<Response>

export interface ConnectToEnvironmentOptions {
  signal?: AbortSignal
  /** Maximum time to wait while Mission Control reports that the environment is waking. */
  wakeTimeoutMs?: number
  /** Reports each observed waking response without changing polling behavior. */
  onProgress?: (progress: MCConnectionProgress) => void
}

interface MCWakingResponse {
  environment_status: 'waking'
}

export interface MCConnectionProgress {
  phase: 'waking-environment'
}

const DEFAULT_WAKE_TIMEOUT_MS = 120_000
const DEFAULT_WAKE_RETRY_MS = 2_000
const MIN_WAKE_RETRY_MS = 1_000
const MAX_WAKE_RETRY_MS = 10_000

function abortReason(signal: AbortSignal): Error {
  return signal.reason instanceof Error
    ? signal.reason
    : new DOMException('Environment connection aborted', 'AbortError')
}

function delay(ms: number, signal?: AbortSignal): Promise<void> {
  if (signal?.aborted) return Promise.reject(abortReason(signal))

  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort)
      resolve()
    }, ms)
    const onAbort = () => {
      clearTimeout(timer)
      if (signal) reject(abortReason(signal))
    }
    signal?.addEventListener('abort', onAbort, {once: true})
  })
}

function isWakingResponse(body: unknown): body is MCWakingResponse {
  return !!body && typeof body === 'object' && 'environment_status' in body && body.environment_status === 'waking'
}

function wakeRetryDelay(response: Response): number {
  const retryAfter = response.headers.get('Retry-After')
  const seconds = retryAfter === null ? Number.NaN : Number(retryAfter)
  const requestedMs = Number.isFinite(seconds) && seconds >= 0 ? seconds * 1_000 : DEFAULT_WAKE_RETRY_MS
  return Math.min(Math.max(requestedMs, MIN_WAKE_RETRY_MS), MAX_WAKE_RETRY_MS)
}

async function readConnectResponse(response: Response, what: string): Promise<MCConnectResponse | MCWakingResponse> {
  if (response.status !== 200 && response.status !== 202) {
    let code: string | undefined
    try {
      const body = (await response.clone().json()) as unknown
      if (body && typeof body === 'object' && 'code' in body && typeof body.code === 'string') code = body.code
    } catch {
      // The status remains useful when the response is empty or not JSON.
    }
    throw new MissionControlError(`${what} returned HTTP ${response.status}`, response.status, code)
  }

  const body = (await response.json()) as unknown
  if (response.status === 202) {
    if (isWakingResponse(body)) return body
    throw new MissionControlError(`${what} returned an invalid waking response`, response.status)
  }
  return assertConnectShape(body as MCConnectResponse, what)
}

function wakingTimeoutError(environmentId: string, wakeTimeoutMs: number): MissionControlError {
  return new MissionControlError(
    `environment ${environmentId} is still waking after ${wakeTimeoutMs}ms`,
    202,
    'wake_timeout',
  )
}

async function readConnectAttempt(
  path: string,
  fetcher: MCFetcher,
  signal: AbortSignal | undefined,
  deadline: number | undefined,
  environmentId: string,
  wakeTimeoutMs: number,
  onProgress: ConnectToEnvironmentOptions['onProgress'],
): Promise<{response: Response; result: MCConnectResponse | MCWakingResponse; deadline: number | undefined}> {
  let activeDeadline = deadline
  const timeoutController = new AbortController()
  const requestSignal = signal ? AbortSignal.any([signal, timeoutController.signal]) : timeoutController.signal
  let timeout: ReturnType<typeof setTimeout> | undefined
  const startTimeout = () => {
    if (activeDeadline === undefined) return
    const remainingMs = activeDeadline - Date.now()
    if (remainingMs <= 0) throw wakingTimeoutError(environmentId, wakeTimeoutMs)
    timeout = setTimeout(() => timeoutController.abort(), remainingMs)
  }

  if (activeDeadline !== undefined) startTimeout()
  try {
    const response = await fetcher(path, {signal: requestSignal})
    if (activeDeadline === undefined && response.status === 202) {
      activeDeadline = Date.now() + wakeTimeoutMs
      startTimeout()
    }
    if (response.status === 202) onProgress?.({phase: 'waking-environment'})
    const result = await readConnectResponse(response, 'connect')
    if (signal?.aborted) throw abortReason(signal)
    if (activeDeadline !== undefined && Date.now() >= activeDeadline) {
      throw wakingTimeoutError(environmentId, wakeTimeoutMs)
    }
    return {response, result, deadline: activeDeadline}
  } catch (error) {
    if (signal?.aborted) throw abortReason(signal)
    if (timeoutController.signal.aborted) throw wakingTimeoutError(environmentId, wakeTimeoutMs)
    throw error
  } finally {
    if (timeout !== undefined) clearTimeout(timeout)
  }
}

/**
 * Validate the fields the WPS transport depends on before building a connection
 * out of them, so a malformed response fails here with a clear error rather
 * than later as an opaque transport failure.
 */
export function assertConnectShape(resp: MCConnectResponse, what: string): MCConnectResponse {
  if (!resp || typeof resp !== 'object') {
    throw new MissionControlError(`${what} returned a non-object response`)
  }
  const missing: string[] = []
  if (!resp.access_token) missing.push('access_token')
  if (!resp.wps_endpoint) missing.push('wps_endpoint')
  if (!resp.client_id) missing.push('client_id')
  if (!resp.groups || typeof resp.groups !== 'object') {
    missing.push('groups')
  } else {
    if (!resp.groups.broadcast) missing.push('groups.broadcast')
    if (!resp.groups.to_client) missing.push('groups.to_client')
    if (!resp.groups.to_host) missing.push('groups.to_host')
  }
  if (missing.length > 0) {
    throw new MissionControlError(`${what} response missing required field(s): ${missing.join(', ')}`)
  }
  assertClientGroups(resp, what)
  return resp
}

function assertClientGroups(resp: MCConnectResponse, what: string): void {
  let expectedIdentity: {uid: string; eid: string} | undefined
  const groups: Array<[keyof MCConnectGroups, ClientLaneKind]> = [
    ['broadcast', 'broadcast'],
    ['to_client', 'to-client'],
    ['to_host', 'to-host'],
  ]
  try {
    for (const [field, expectedLane] of groups) {
      const parsed = parseGroupName(resp.groups[field], {
        expected: {...expectedIdentity, cid: resp.client_id},
      })
      if (parsed.scope !== 'client' || parsed.lane !== expectedLane) {
        throw new Error(`groups.${field} is not the ${expectedLane} client lane`)
      }
      expectedIdentity ??= {uid: parsed.uid, eid: parsed.eid}
    }
  } catch (error) {
    throw new MissionControlError(
      `${what} response contains invalid client groups: ${error instanceof Error ? error.message : String(error)}`,
    )
  }
}

/**
 * Request WPS credentials for an environment. Mission Control mints a fresh
 * token + client_id and notifies the environment's daemon that a client has
 * connected. Session creation remains an explicit AHP request.
 */
export async function connectToEnvironment(
  environmentId: string,
  sessionId: string,
  fetcher: MCFetcher,
  options: ConnectToEnvironmentOptions = {},
): Promise<MCConnectResponse> {
  const {signal, wakeTimeoutMs = DEFAULT_WAKE_TIMEOUT_MS, onProgress} = options
  const path = environmentConnectApiPath(environmentId, sessionId)
  let deadline: number | undefined

  for (;;) {
    if (signal?.aborted) throw abortReason(signal)
    const attempt = await readConnectAttempt(path, fetcher, signal, deadline, environmentId, wakeTimeoutMs, onProgress)
    const {response, result} = attempt
    deadline = attempt.deadline
    if (!isWakingResponse(result)) return result

    deadline ??= Date.now() + wakeTimeoutMs
    const remainingMs = deadline - Date.now()
    if (remainingMs <= 0) {
      throw wakingTimeoutError(environmentId, wakeTimeoutMs)
    }
    const retryAfterMs = Math.min(wakeRetryDelay(response), remainingMs)
    await delay(retryAfterMs, signal)
  }
}

/** Refresh the WPS token for an existing relay client connection. */
export async function reconnectToEnvironment(
  environmentId: string,
  clientId: string,
  fetcher: MCFetcher,
  options: MCFetchOptions = {},
): Promise<MCConnectResponse> {
  const path = environmentReconnectApiPath(environmentId, clientId)
  const response = await fetcher(path, options)
  const result = await readConnectResponse(response, 'reconnect')
  if (isWakingResponse(result)) {
    throw new MissionControlError('reconnect unexpectedly reported a waking environment', response.status)
  }
  if (result.client_id !== clientId) {
    throw new MissionControlError(
      `reconnect changed client_id from '${clientId}' to '${result.client_id}'`,
      response.status,
    )
  }
  return result
}

/**
 * Build the WPS WebSocket URL from a connect/reconnect response: the endpoint
 * with the access token and Mission Control-minted client identity attached,
 * and a `ws(s)://` scheme guaranteed.
 */
export function buildWpsUrl(resp: Pick<MCConnectResponse, 'wps_endpoint' | 'access_token' | 'client_id'>): string {
  let raw = resp.wps_endpoint
  if (!raw.startsWith('wss://') && !raw.startsWith('ws://')) {
    raw = `wss://${raw}`
  }
  // wps_endpoint is always an absolute ws(s):// URL, so no base is required.
  // eslint-disable-next-line no-restricted-syntax
  const url = new URL(raw)
  url.searchParams.set('access_token', resp.access_token)
  url.searchParams.set('clientId', resp.client_id)
  return url.toString()
}
